import {
  COUPON_LOCKED_IN_EDIT_FIELDS,
  COUPON_PREVIEW_NOT_SET,
  DISCOUNT_TYPES,
  ZERO,
} from "constants/coupons";
import { formatAmountWithCurrencyPrefix } from "helpers/couponDiscountType";
import { formatCouponDate } from "utils/couponUtils";
import { parsePackageIds } from "helpers/packageIds";

export const STATUS_DOT_CLASS_SUFFIX = {
  ACTIVE: "statusDotActive",
  INACTIVE: "statusDotInactive",
  EXPIRED: "statusDotExpired",
  SCHEDULED: "statusDotScheduled",
};

export const STATUS_BADGE_CLASS_SUFFIX = {
  ACTIVE: "summaryStatusActive",
  INACTIVE: "summaryStatusInactive",
  EXPIRED: "summaryStatusExpired",
  SCHEDULED: "summaryStatusScheduled",
};

export const isFieldLocked = (mode, fieldName) => {
  if (mode === "view") return true;
  if (mode === "edit") return COUPON_LOCKED_IN_EDIT_FIELDS.includes(fieldName);
  return false;
};

export const isEmptyPreviewValue = (value) =>
  value === null || value === undefined || value === "";

export const getPreviewText = (value) =>
  isEmptyPreviewValue(value) ? COUPON_PREVIEW_NOT_SET : String(value);

export const getDiscountDisplay = (values, currencyOptions = []) => {
  const formattedValue = formatDiscountFieldValue(values, currencyOptions);

  if (formattedValue === COUPON_PREVIEW_NOT_SET) {
    return COUPON_PREVIEW_NOT_SET;
  }

  return `${formattedValue} OFF`;
};

export const formatDisplayDate = (dateStr) => {
  if (dateStr === "" || dateStr === null || dateStr === undefined) {
    return "";
  }

  return formatCouponDate(dateStr) === "-"
    ? String(dateStr)
    : formatCouponDate(dateStr);
};

export const formatDiscountFieldValue = (values, currencyOptions = []) => {
  if (values.discountType === DISCOUNT_TYPES.PERCENTAGE) {
    if (isEmptyPreviewValue(values.discountValue)) {
      return COUPON_PREVIEW_NOT_SET;
    }

    return `${values.discountValue}%`;
  }

  const currencySettings = values.currencySettings ?? {};
  const currencyIds = Object.keys(currencySettings);

  if (currencyIds.length > ZERO) {
    const formattedAmounts = currencyIds
      .map((currencyId) => {
        const amount = currencySettings[currencyId]?.discountAmount;
        if (isEmptyPreviewValue(amount)) {
          return null;
        }

        return (
          formatAmountWithCurrencyPrefix(amount, currencyOptions, currencyId) ||
          String(amount)
        );
      })
      .filter(Boolean);

    if (formattedAmounts.length > ZERO) {
      return formattedAmounts.join(", ");
    }
  }

  if (isEmptyPreviewValue(values.discountValue)) {
    return COUPON_PREVIEW_NOT_SET;
  }

  return (
    formatAmountWithCurrencyPrefix(
      values.discountValue,
      currencyOptions,
      values.currencyId
    ) || COUPON_PREVIEW_NOT_SET
  );
};

export const getPreviewDateValue = (dateStr) =>
  isEmptyPreviewValue(dateStr) ? COUPON_PREVIEW_NOT_SET : formatCouponDate(dateStr);

export const getPreviewCreatedOnDisplay = (values, isCreate) => {
  if (!isEmptyPreviewValue(values.createdOn)) {
    return formatCouponDate(values.createdOn);
  }

  if (isCreate) {
    return formatCouponDate(new Date());
  }

  return COUPON_PREVIEW_NOT_SET;
};

export const hasPreviewCreatedOn = (values, isCreate) =>
  !isEmptyPreviewValue(values.createdOn) || isCreate;

export const formatPreviewAmount = (
  value,
  currencyOptions = [],
  currencyId = ""
) => formatAmountWithCurrencyPrefix(value, currencyOptions, currencyId) ||
  (value === "" || value === null || value === undefined
    ? COUPON_PREVIEW_NOT_SET
    : String(value));

export const getPreviewSubtitle = (values) => {
  if (values.selectAllPackages) {
    return "On all packages";
  }

  if (parsePackageIds(values.packageIds).length > ZERO) {
    return "On selected packages";
  }

  return COUPON_PREVIEW_NOT_SET;
};

export const getStepConfig = (stepId, steps) =>
  steps.find((step) => step.id === stepId) ?? {};
